import {
  differenceInDays,
  format,
  isFuture,
  isPast,
  isWithinInterval,
} from "date-fns";
import {
  Archive,
  Calendar,
  Edit2,
  Eye,
  Filter,
  Heart,
  MapPin,
  Plane,
  Plus,
  Search,
  ShieldAlert,
  Syringe,
  Trash2,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { ReadinessRing } from "../../components/ui/ReadinessRing";
import {
  getReadinessSummaries,
  type ReadinessSummary,
} from "../../services/api/travelHealth";
import { PageBanner } from "../../components/ui/PageBanner";
import {
  archiveTravel,
  deleteTravel,
  getTravels,
  Travel,
} from "../../services/api/travels";
import { useI18nStore } from "../../stores/i18nStore";

export const TravelsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18nStore();
  const [travels, setTravels] = useState<Travel[]>([]);
  const [filteredTravels, setFilteredTravels] = useState<Travel[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [purposeFilter, setPurposeFilter] = useState<string>("all");
  const [readiness, setReadiness] = useState<Record<string, ReadinessSummary>>(
    {},
  );

  useEffect(() => {
    loadTravels();
  }, []);

  useEffect(() => {
    filterTravels();
  }, [travels, searchQuery, statusFilter, purposeFilter]);

  const loadTravels = async () => {
    try {
      setLoading(true);
      const [data, summaries] = await Promise.all([
        getTravels(),
        getReadinessSummaries().catch(() => ({})),
      ]);
      setTravels(data);
      setReadiness(summaries);
    } catch (error: any) {
      console.error("Failed to load travels:", error);
      toast.error("Failed to load travels");
    } finally {
      setLoading(false);
    }
  };

  const filterTravels = () => {
    let filtered = [...travels];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (travel) =>
          travel.destination?.toLowerCase().includes(query) ||
          travel.country?.toLowerCase().includes(query) ||
          travel.notes?.toLowerCase().includes(query),
      );
    }

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((travel) => travel.status === statusFilter);
    }

    // Purpose filter
    if (purposeFilter !== "all") {
      filtered = filtered.filter((travel) => travel.purpose === purposeFilter);
    }

    setFilteredTravels(filtered);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t.confirmDeleteTravel)) return;

    try {
      await deleteTravel(id);
      toast.success(t.travelDeleted);
      loadTravels();
    } catch (error: any) {
      console.error("Failed to delete travel:", error);
      toast.error("Failed to delete travel");
    }
  };

  // Health indicators. These were mock values until 2026-10-06: risk from a
  // hardcoded country list, and vaccination and recommendation counts from
  // Math.random(), drawn anew on every render. The tiles now link to the pages
  // with the real data, and readiness is the trip's own checklist
  // (backend/src/services/tripReadiness.ts).
  const readinessPercent = (travel: Travel): number | null =>
    readiness[travel._id]?.percent ?? null;

  const handleArchive = async (id: string) => {
    try {
      await archiveTravel(id);
      toast.success(t.travelArchived);
      loadTravels();
    } catch (error: any) {
      console.error("Failed to archive travel:", error);
      toast.error("Failed to archive travel");
    }
  };

  const getTravelTimeStatus = (travel: Travel) => {
    if (!travel.departureDate || !travel.returnDate) {
      return { label: t.statusDraft, color: "text-gray-400", days: null };
    }

    const now = new Date();
    const departure = new Date(travel.departureDate);
    const returnDate = new Date(travel.returnDate);

    if (isPast(returnDate)) {
      return { label: t.returned, color: "text-gray-500", days: null };
    }

    if (isWithinInterval(now, { start: departure, end: returnDate })) {
      const daysLeft = differenceInDays(returnDate, now);
      return { label: t.inProgress, color: "text-primary-600", days: daysLeft };
    }

    if (isFuture(departure)) {
      const daysUntil = differenceInDays(departure, now);
      return {
        label: t.daysUntilDeparture,
        color: "text-green-600",
        days: daysUntil,
      };
    }

    return { label: "", color: "", days: null };
  };

  const getStatusBadgeColor = (status: string) => {
    const colors: Record<string, string> = {
      draft: "bg-gray-100 text-gray-800",
      planned: "bg-primary-100 text-primary-800",
      current: "bg-green-100 text-green-800",
      completed: "bg-purple-100 text-purple-800",
      cancelled: "bg-red-100 text-red-800",
      archived: "bg-gray-100 text-gray-600",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const getStatusLabel = (status: string) => {
    const statusMap: Record<string, string> = {
      draft: t.statusDraft,
      planned: t.statusPlanned,
      current: t.statusCurrent,
      completed: t.statusCompleted,
      cancelled: t.statusCancelled,
      archived: t.statusArchived,
    };
    return statusMap[status] || status;
  };

  const getPurposeLabel = (purpose?: string) => {
    if (!purpose) return t.purposeOther;
    const purposeMap: Record<string, string> = {
      leisure: t.purposeLeisure,
      business: t.purposeBusiness,
      medical: t.purposeMedical,
      education: t.purposeEducation,
      volunteering: t.purposeVolunteering,
      other: t.purposeOther,
    };
    return purposeMap[purpose] || purpose;
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto space-y-8">
        <PageBanner
          icon={Plane}
          title={t.travels}
          description={t.planYourTrips}
        />
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">{t.loading}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      {/* Page Banner */}
      <PageBanner
        icon={Plane}
        title={t.myTravels}
        description={t.planYourTrips}
      />

      {/* Action Bar */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder={t.searchTravels}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white"
            >
              <option value="all">{t.showAll}</option>
              <option value="planned">{t.statusPlanned}</option>
              <option value="current">{t.statusCurrent}</option>
              <option value="completed">{t.statusCompleted}</option>
              <option value="draft">{t.statusDraft}</option>
            </select>
          </div>

          {/* Purpose Filter */}
          <div className="relative">
            <select
              value={purposeFilter}
              onChange={(e) => setPurposeFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white"
            >
              <option value="all">{t.filterByPurpose}</option>
              <option value="leisure">{t.purposeLeisure}</option>
              <option value="business">{t.purposeBusiness}</option>
              <option value="medical">{t.purposeMedical}</option>
              <option value="education">{t.purposeEducation}</option>
              <option value="volunteering">{t.purposeVolunteering}</option>
              <option value="other">{t.purposeOther}</option>
            </select>
          </div>

          {/* New Travel Button */}
          <button
            onClick={() => navigate("/traveler/travels/new")}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors whitespace-nowrap"
          >
            <Plus className="h-5 w-5" />
            {t.newTravel}
          </button>
        </div>
      </div>

      {/* Travels List */}
      {filteredTravels.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Plane className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {t.noTravelsYet}
          </h3>
          <p className="text-gray-600 mb-6">{t.startPlanningTravel}</p>
          <button
            onClick={() => navigate("/traveler/travels/new")}
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="h-5 w-5" />
            {t.newTravel}
          </button>
        </div>
      ) : (
        <div className="grid gap-6">
          {filteredTravels.map((travel) => {
            const timeStatus = getTravelTimeStatus(travel);
            return (
              <div
                key={travel._id}
                className="bg-white rounded-lg shadow hover:shadow-md transition-shadow"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-semibold text-gray-900">
                          {travel.destination}
                        </h3>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(
                            travel.status,
                          )}`}
                        >
                          {getStatusLabel(travel.status)}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <MapPin className="h-4 w-4 shrink-0" />
                          {travel.country || "N/A"}
                        </div>
                        {travel.departureDate && travel.returnDate && (
                          <div className="flex items-center gap-1 whitespace-nowrap">
                            <Calendar className="h-4 w-4 shrink-0" />
                            {format(
                              new Date(travel.departureDate),
                              "MMM dd, yyyy",
                            )}{" "}
                            -{" "}
                            {format(
                              new Date(travel.returnDate),
                              "MMM dd, yyyy",
                            )}
                          </div>
                        )}
                        {travel.purpose && (
                          <div className="text-gray-500">
                            • {getPurposeLabel(travel.purpose)}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        className="p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                        title={t.viewTravel}
                        onClick={() =>
                          navigate(`/traveler/travels/${travel._id}`)
                        }
                      >
                        <Eye className="h-5 w-5" />
                      </button>
                      <button
                        className="p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                        title={t.editTravel}
                        onClick={() =>
                          navigate(`/traveler/travels/${travel._id}`)
                        }
                      >
                        <Edit2 className="h-5 w-5" />
                      </button>
                      <button
                        className="p-2 text-gray-600 hover:text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
                        onClick={() => handleArchive(travel._id!)}
                        title={t.archiveTravel}
                      >
                        <Archive className="h-5 w-5" />
                      </button>
                      <button
                        className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        onClick={() => handleDelete(travel._id!)}
                        title={t.deleteTravel}
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>

                  {/* Health Indicators Section */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {/* Health Risk Radar */}
                      <div
                        role="button"
                        tabIndex={0}
                        className="flex flex-col items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                        onClick={() =>
                          navigate(
                            `/traveler/travel/${travel._id}/riskmanagement`,
                          )
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            navigate(
                              `/traveler/travel/${travel._id}/riskmanagement`,
                            );
                          }
                        }}
                      >
                        <div className="w-12 h-12 bg-travel-100 rounded-full flex items-center justify-center">
                          <ShieldAlert className="h-6 w-6 text-travel-600" />
                        </div>
                        <div className="mt-2 text-center">
                          <div className="text-xs font-medium text-gray-700">
                            {t.healthRisk || "Health Risk"}
                          </div>
                        </div>
                      </div>

                      {/* Required Vaccinations */}
                      <div
                        role="button"
                        tabIndex={0}
                        className="flex flex-col items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                        onClick={() =>
                          navigate(
                            `/traveler/travel/${travel._id}/vaccinations`,
                          )
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            navigate(
                              `/traveler/travel/${travel._id}/vaccinations`,
                            );
                          }
                        }}
                      >
                        <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                          <Syringe className="h-6 w-6 text-primary-600" />
                        </div>
                        <div className="mt-2 text-center">
                          <div className="text-xs font-medium text-gray-700">
                            {t.vaccinations || "Vaccinations"}
                          </div>
                        </div>
                      </div>

                      {/* Travel Readiness */}
                      <div
                        role="button"
                        tabIndex={0}
                        className="flex flex-col items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                        onClick={() =>
                          navigate(`/traveler/travel/${travel._id}/readiness`)
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            navigate(
                              `/traveler/travel/${travel._id}/readiness`,
                            );
                          }
                        }}
                      >
                        <ReadinessRing
                          percent={readinessPercent(travel)}
                          size={48}
                        />
                        <div className="mt-2 text-center">
                          <div className="text-xs font-medium text-gray-700">
                            {t.travelReadiness || "Readiness"}
                          </div>
                          <div className="text-xs text-gray-500">
                            {readinessPercent(travel) === null
                              ? t.readinessNotChecked
                              : readinessPercent(travel) === 100
                                ? t.readinessAllDone
                                : t.readinessCaption}
                          </div>
                        </div>
                      </div>

                      {/* Personal Recommendations */}
                      <div
                        role="button"
                        tabIndex={0}
                        className="flex flex-col items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                        onClick={() =>
                          navigate(
                            `/traveler/travel/${travel._id}/recommendations`,
                          )
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            navigate(
                              `/traveler/travel/${travel._id}/recommendations`,
                            );
                          }
                        }}
                      >
                        <div className="w-12 h-12 bg-health-100 rounded-full flex items-center justify-center">
                          <Heart className="h-6 w-6 text-health-600" />
                        </div>
                        <div className="mt-2 text-center">
                          <div className="text-xs font-medium text-gray-700">
                            {t.recommendations || "Recommendations"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {timeStatus.days !== null && (
                    <div
                      className={`mt-4 text-sm ${timeStatus.color} font-medium`}
                    >
                      {timeStatus.label.replace(
                        "{days}",
                        timeStatus.days.toString(),
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
